import { PageCanvas } from "@/components/page-canvas";
import { useEffect, useRef, useState } from "react";
import { Head, Link, router, useForm, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { FormErrors, errorText } from "@/components/forms/form-errors";
import {
  locationJobProductConsumptionsPath,
  newLocationJobProductConsumptionPath,
  locationDetailingProductStockAdjustmentsPath,
} from "@/routes";
import { quantity, type Product } from "../inventory/types";

type Kind = "receipt" | "correction";
export default function ConsumptionForm({
  job_id,
  entry_key,
  products,
  selected_product_id,
  adjustment,
}: {
  job_id: string;
  entry_key: string;
  products: Product[];
  selected_product_id: string | null;
  adjustment: Kind | null;
}) {
  const { auth } = usePage().props;
  const { t, i18n } = useTranslation();
  const locationId = auth.location?.id || "";
  const form = useForm(`Consumption:${locationId}:${job_id}:${entry_key}`, {
    product_consumption: {
      product_id: selected_product_id || products[0]?.id || "",
      quantity: "",
      reason: "",
      entry_key,
    },
  });
  const [kind, setKind] = useState<Kind | null>(adjustment);
  const trigger = useRef<HTMLButtonElement>(null);
  const product = products.find(
    (item) => item.id === form.data.product_consumption.product_id,
  );
  const stock = useForm({
    revision: product?.balance_revision || "",
    consumption: { job_id, entry_key },
    stock_adjustment: {
      kind: adjustment || "receipt",
      quantity: adjustment === "correction" ? product?.quantity || "" : "",
      reason: "",
    },
  });
  useEffect(() => {
    if (adjustment) setKind(adjustment);
  }, [adjustment]);
  if (!auth.location) return null;
  const values = form.data.product_consumption;
  const openStock = (value: Kind) => {
    if (!product) return;
    stock.setData({
      revision: product.balance_revision,
      consumption: { job_id, entry_key },
      stock_adjustment: {
        kind: value,
        quantity: value === "correction" ? product.quantity : "",
        reason: "",
      },
    });
    stock.clearErrors();
    setKind(value);
    router.get(
      newLocationJobProductConsumptionPath(locationId, job_id, {
        entry_key,
        product_id: product.id,
        adjustment: value,
      }),
      {},
      { preserveState: true, preserveScroll: true },
    );
  };
  const close = () => {
    setKind(null);
    stock.resetAndClearErrors();
    router.get(
      newLocationJobProductConsumptionPath(locationId, job_id, {
        entry_key,
        product_id: product?.id,
      }),
      {},
      { preserveState: true, preserveScroll: true, replace: true },
    );
  };
  const errors = form.errors as Record<string, string>;
  const stockErrors = stock.errors as Record<string, string>;
  const targets = {
    quantity: "consumption-quantity",
    reason: "consumption-reason",
    product: "consumption-product",
  };
  return (
    <JobWorkspace primaryAction={false}>
      <Head title={t("product_consumptions.record")} />
      <PageCanvas padded={false}>
        <h2 className="text-xl font-semibold">
          {t("product_consumptions.record")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("product_consumptions.help")}
        </p>
        {!products.length ? (
          <p>{t("product_consumptions.no_products")}</p>
        ) : (
          <form
            className="max-w-2xl space-y-5"
            onSubmit={(event) => {
              event.preventDefault();
              form.post(locationJobProductConsumptionsPath(locationId, job_id));
            }}
          >
            <FormErrors errors={form.errors} targets={targets} />
            <div className="space-y-2">
              <Label htmlFor="consumption-product">
                {t("product_consumptions.product")}
              </Label>
              <NativeSelect
                id="consumption-product"
                autoFocus
                className="w-full"
                value={values.product_id}
                onChange={(event) =>
                  form.setData("product_consumption", {
                    ...values,
                    product_id: event.target.value,
                  })
                }
              >
                {products.map((item) => (
                  <NativeSelectOption key={item.id} value={item.id}>
                    {item.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
            {product && (
              <>
                <p role="status">
                  {t("inventory.balance")}:{" "}
                  {quantity(product.quantity, i18n.language)}{" "}
                  {t(`inventory.${product.unit}`)}
                </p>
                <div className="space-y-2">
                  <Label htmlFor="consumption-quantity">
                    {t("product_consumptions.quantity")} (
                    {t(`inventory.${product.unit}`)})
                  </Label>
                  <Input
                    id="consumption-quantity"
                    required
                    inputMode={product.unit === "litre" ? "decimal" : "numeric"}
                    value={values.quantity}
                    onChange={(event) =>
                      form.setData("product_consumption", {
                        ...values,
                        quantity: event.target.value,
                      })
                    }
                    aria-invalid={!!errors.quantity}
                    aria-describedby="consumption-precision consumption-quantity-error"
                  />
                  <p
                    id="consumption-precision"
                    className="text-muted-foreground text-sm"
                  >
                    {t(`inventory.precision_${product.unit}`)}
                  </p>
                  {errors.quantity && (
                    <p
                      id="consumption-quantity-error"
                      className="text-destructive text-sm"
                    >
                      {errorText(errors.quantity)}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="consumption-reason">
                    {t("inventory.reason")}
                  </Label>
                  <Textarea
                    id="consumption-reason"
                    maxLength={500}
                    required
                    value={values.reason}
                    onChange={(event) =>
                      form.setData("product_consumption", {
                        ...values,
                        reason: event.target.value,
                      })
                    }
                    aria-invalid={!!errors.reason}
                  />
                  {errors.reason && (
                    <p className="text-destructive text-sm">
                      {errorText(errors.reason)}
                    </p>
                  )}
                </div>
                <p className="text-muted-foreground text-sm">
                  {t("product_consumptions.correction_help")}
                </p>
                <div className="flex flex-wrap gap-3">
                  {(["receipt", "correction"] as const).map((value) => (
                    <Button
                      key={value}
                      ref={value === "correction" ? trigger : undefined}
                      type="button"
                      variant="outline"
                      onClick={() => openStock(value)}
                    >
                      {t(`inventory.${value}`)}
                    </Button>
                  ))}
                </div>
              </>
            )}
            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={form.processing || !product}>
                {t("product_consumptions.record")}
              </Button>
              <Button
                variant="outline"
                render={
                  <Link
                    href={locationJobProductConsumptionsPath(
                      locationId,
                      job_id,
                    )}
                  />
                }
              >
                {t("inventory.cancel")}
              </Button>
            </div>
          </form>
        )}
      </PageCanvas>
      <Sheet
        open={!!kind}
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        <SheetContent
          className="w-full! overflow-y-auto sm:max-w-lg!"
          finalFocus={trigger}
        >
          <SheetHeader>
            <SheetTitle>{t(`inventory.${kind || "receipt"}`)}</SheetTitle>
            <SheetDescription>
              {t("workflow.stock_return_help")}
            </SheetDescription>
          </SheetHeader>
          {product && (
            <form
              className="space-y-5 px-4 pb-4"
              onSubmit={(event) => {
                event.preventDefault();
                stock.post(
                  locationDetailingProductStockAdjustmentsPath(
                    locationId,
                    product.id,
                  ),
                  {
                    preserveScroll: true,
                    errorBag: "stock",
                    onSuccess: () => {
                      setKind(null);
                      stock.resetAndClearErrors();
                    },
                  },
                );
              }}
            >
              <p className="font-medium">{product.name}</p>
              <p>
                {t("inventory.balance")}:{" "}
                {quantity(product.quantity, i18n.language)}{" "}
                {t(`inventory.${product.unit}`)}
              </p>
              {kind === "correction" &&
                stock.data.revision !== product.balance_revision && (
                  <div className="space-y-3 rounded-md border p-3">
                    <p>{t("workflow.stock_changed")}</p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        stock.setData("revision", product.balance_revision)
                      }
                    >
                      {t("workflow.review_stock")}
                    </Button>
                  </div>
                )}
              <FormErrors
                errors={stock.errors}
                targets={{ quantity: "stock-quantity", reason: "stock-reason" }}
              />
              <div className="space-y-2">
                <Label htmlFor="stock-quantity">
                  {t(
                    kind === "correction"
                      ? "inventory.count"
                      : "inventory.quantity",
                  )}{" "}
                  ({t(`inventory.${product.unit}`)})
                </Label>
                <Input
                  id="stock-quantity"
                  autoFocus
                  required
                  inputMode={product.unit === "litre" ? "decimal" : "numeric"}
                  value={stock.data.stock_adjustment.quantity}
                  onChange={(event) =>
                    stock.setData("stock_adjustment", {
                      ...stock.data.stock_adjustment,
                      quantity: event.target.value,
                    })
                  }
                  aria-invalid={!!stockErrors.quantity}
                />
                {stockErrors.quantity && (
                  <p className="text-sm text-destructive">
                    {errorText(stockErrors.quantity)}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  {t(`inventory.precision_${product.unit}`)}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="stock-reason">{t("inventory.reason")}</Label>
                <Textarea
                  id="stock-reason"
                  required
                  maxLength={500}
                  value={stock.data.stock_adjustment.reason}
                  onChange={(event) =>
                    stock.setData("stock_adjustment", {
                      ...stock.data.stock_adjustment,
                      reason: event.target.value,
                    })
                  }
                  aria-invalid={!!stockErrors.reason}
                />
                {stockErrors.reason && (
                  <p className="text-sm text-destructive">
                    {errorText(stockErrors.reason)}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  type="submit"
                  disabled={
                    stock.processing ||
                    (kind === "correction" &&
                      stock.data.revision !== product.balance_revision)
                  }
                >
                  {t("inventory.save")}
                </Button>
                <Button type="button" variant="outline" onClick={close}>
                  {t("inventory.cancel")}
                </Button>
              </div>
            </form>
          )}
        </SheetContent>
      </Sheet>
    </JobWorkspace>
  );
}
